import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import api from 'lib/api';
import { format } from 'date-fns';

function SkeletonBlock({ className }) {
  return <div className={`bg-[var(--color-muted)] animate-pulse rounded ${className}`} />;
}

const COMMON_EXERCISES = [
  'Bench Press', 'Squat', 'Deadlift', 'Pull-ups', 'Shoulder Press',
  'Barbell Row', 'Overhead Press', 'Leg Press', 'Bicep Curl', 'Tricep Dip',
  '5K Run', '10K Run', 'Plank', 'Push-ups', 'Dips'
];

const UNITS = ['kg', 'lbs', 'reps', 'seconds', 'minutes'];

export default function PersonalRecords({ memberId }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('recent');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [newPR, setNewPR] = useState(null);
  const [form, setForm] = useState({
    exercise_name: '', custom_exercise: '', record_value: '', unit: 'kg', achieved_at: format(new Date(), 'yyyy-MM-dd'), notes: ''
  });

  const fetchRecords = useCallback(async () => {
    if (!memberId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      setRecords([]);
    } catch (e) {
      setError(e?.message ?? 'Failed to load personal records');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const sortedRecords = [...(records ?? [])]?.sort((a, b) => {
    if (sortBy === 'recent') return new Date(b?.achieved_at) - new Date(a?.achieved_at);
    return (a?.exercise_name ?? '')?.localeCompare(b?.exercise_name ?? '');
  });

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!memberId) return;
    const exerciseName = form?.exercise_name === '__custom__' ? form?.custom_exercise : form?.exercise_name;
    if (!exerciseName || !form?.record_value) {
      setFormError('Exercise name and value are required');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    setNewPR(null);
    try {
      throw new Error('Personal record logging is not available in this version');
      setShowForm(false);
      setForm({ exercise_name: '', custom_exercise: '', record_value: '', unit: 'kg', achieved_at: format(new Date(), 'yyyy-MM-dd'), notes: '' });
      fetchRecords();
    } catch (e) {
      setFormError(e?.message ?? 'Failed to save record');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6]?.map(i => <SkeletonBlock key={i} className="h-32" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-[var(--color-error)]/5 border border-[var(--color-error)]/20 rounded-[var(--radius-md)]">
        <Icon name="AlertCircle" size={16} color="var(--color-error)" />
        <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* New PR Banner */}
      {newPR && (
        <div className="flex items-center gap-3 p-4 bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 rounded-[var(--radius-md)]">
          <span className="text-2xl">🏆</span>
          <div>
            <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">New Personal Record!</p>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">You set a new PR for {newPR}!</p>
          </div>
          <button onClick={() => setNewPR(null)} className="ml-auto">
            <Icon name="X" size={14} color="var(--color-muted-foreground)" />
          </button>
        </div>
      )}
      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Sort by:</span>
          <div className="flex bg-[var(--color-muted)] rounded-[var(--radius-sm)] p-1">
            {[{ id: 'recent', label: 'Most Recent' }, { id: 'name', label: 'Exercise Name' }]?.map(s => (
              <button
                key={s?.id}
                onClick={() => setSortBy(s?.id)}
                className={`px-3 py-1 rounded text-xs font-medium font-[var(--font-caption)] transition-all ${
                  sortBy === s?.id ? 'bg-[var(--color-card)] text-[var(--color-foreground)] shadow-sm' : 'text-[var(--color-muted-foreground)]'
                }`}
              >
                {s?.label}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity"
        >
          <Icon name="Plus" size={14} color="#fff" />
          Log New PR
        </button>
      </div>
      {/* Log Form */}
      {showForm && (
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
          <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)] mb-4">Log Personal Record</h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1">Exercise *</label>
                <select
                  value={form?.exercise_name}
                  onChange={e => setForm(prev => ({ ...prev, exercise_name: e?.target?.value }))}
                  className="w-full px-3 py-2 bg-[var(--color-input)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
                  required
                >
                  <option value="">Select exercise...</option>
                  {COMMON_EXERCISES?.map(ex => <option key={ex} value={ex}>{ex}</option>)}
                  <option value="__custom__">Custom exercise...</option>
                </select>
              </div>
              {form?.exercise_name === '__custom__' && (
                <div>
                  <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1">Custom Exercise Name *</label>
                  <input
                    type="text" placeholder="e.g. Farmer's Walk"
                    value={form?.custom_exercise}
                    onChange={e => setForm(prev => ({ ...prev, custom_exercise: e?.target?.value }))}
                    className="w-full px-3 py-2 bg-[var(--color-input)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1">Value *</label>
                <input
                  type="number" step="0.1" placeholder="e.g. 100"
                  value={form?.record_value}
                  onChange={e => setForm(prev => ({ ...prev, record_value: e?.target?.value }))}
                  className="w-full px-3 py-2 bg-[var(--color-input)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1">Unit</label>
                <select
                  value={form?.unit}
                  onChange={e => setForm(prev => ({ ...prev, unit: e?.target?.value }))}
                  className="w-full px-3 py-2 bg-[var(--color-input)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
                >
                  {UNITS?.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1">Date Achieved</label>
                <input
                  type="date"
                  value={form?.achieved_at}
                  onChange={e => setForm(prev => ({ ...prev, achieved_at: e?.target?.value }))}
                  className="w-full px-3 py-2 bg-[var(--color-input)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1">Notes (optional)</label>
                <input
                  type="text" placeholder="Any notes..."
                  value={form?.notes}
                  onChange={e => setForm(prev => ({ ...prev, notes: e?.target?.value }))}
                  className="w-full px-3 py-2 bg-[var(--color-input)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
                />
              </div>
            </div>
            {formError && <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)] mb-3">{formError}</p>}
            <div className="flex gap-3">
              <button
                type="submit" disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Save" size={14} color="#fff" />}
                {submitting ? 'Saving...' : 'Save Record'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] font-[var(--font-caption)] transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      {/* Records Grid */}
      {records?.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 py-20 bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)]">
          <Icon name="Trophy" size={32} color="var(--color-muted-foreground)" />
          <div className="text-center">
            <h3 className="text-lg font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">No personal records yet</h3>
            <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">Log your first PR to start tracking your bests!</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedRecords?.map(r => {
            const improvement = r?.previous_value ? parseFloat((r?.record_value - r?.previous_value)?.toFixed(1)) : null;
            return (
              <div key={r?.id} className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-4 shadow-[var(--shadow-sm)] hover:border-[var(--color-primary)]/40 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-[var(--radius-sm)] bg-[var(--color-warning)]/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Trophy" size={18} color="var(--color-warning)" />
                  </div>
                  {improvement !== null && improvement > 0 && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-[var(--color-success)]/10 text-[var(--color-success)] rounded-full text-xs font-medium font-[var(--font-caption)]">
                      <Icon name="TrendingUp" size={11} color="currentColor" />
                      +{improvement} {r?.unit}
                    </span>
                  )}
                </div>
                <h4 className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-1 truncate">{r?.exercise_name}</h4>
                <p className="text-2xl font-bold text-[var(--color-primary)] font-[var(--font-heading)]">
                  {r?.record_value} <span className="text-base font-medium text-[var(--color-muted-foreground)]">{r?.unit}</span>
                </p>
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-2">
                  {format(new Date(r?.achieved_at), 'MMM d, yyyy')}
                </p>
                {r?.previous_value && (
                  <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
                    Previous: {r?.previous_value} {r?.unit}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
